import versionInfo from '../../version.json';

export interface VersionInfo {
  version: string;
  releaseDate: string;
  environment: string;
  features: {
    kanban: boolean;
    pmo: boolean;
    financial: boolean;
    gantt: boolean;
  };
  buildInfo: {
    timestamp: string;
    commitHash: string;
    branch: string;
  };
}

export interface ChangelogEntry {
  version: string;
  date: string;
  type: 'major' | 'minor' | 'patch';
  changes: {
    added?: string[];
    changed?: string[];
    deprecated?: string[];
    removed?: string[];
    fixed?: string[];
    security?: string[];
  };
}

class VersionService {
  private currentVersion: VersionInfo;
  private lastCheckedVersion: string | null = null;
  private updateAvailable: boolean = false;

  constructor() {
    this.currentVersion = versionInfo as VersionInfo;
    this.lastCheckedVersion = localStorage.getItem('lastKnownVersion');
    this.checkForUpdates();
  }

  getCurrentVersion(): VersionInfo {
    return this.currentVersion;
  }

  getVersionString(): string {
    return this.currentVersion.version;
  }

  getFormattedVersion(): string {
    const { version, environment } = this.currentVersion;
    return environment === 'production' ? `v${version}` : `v${version}-${environment}`;
  }

  getBuildDate(): Date {
    return new Date(this.currentVersion.buildInfo.timestamp || this.currentVersion.releaseDate);
  }

  getFeatureFlags(): typeof this.currentVersion.features {
    return this.currentVersion.features;
  }

  isFeatureEnabled(feature: keyof typeof this.currentVersion.features): boolean {
    return this.currentVersion.features[feature] || false;
  }

  private checkForUpdates(): void {
    if (this.lastCheckedVersion && this.lastCheckedVersion !== this.currentVersion.version) {
      this.updateAvailable = true;
      this.notifyUpdate();
    }
    localStorage.setItem('lastKnownVersion', this.currentVersion.version);
  }

  private notifyUpdate(): void {
    window.dispatchEvent(new CustomEvent('version-update', {
      detail: {
        oldVersion: this.lastCheckedVersion,
        newVersion: this.currentVersion.version,
        releaseDate: this.currentVersion.releaseDate
      }
    }));
  }

  hasUpdate(): boolean {
    return this.updateAvailable;
  }

  dismissUpdate(): void {
    this.updateAvailable = false;
    this.lastCheckedVersion = this.currentVersion.version;
  }

  compareVersions(v1: string, v2: string): number {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);

    for (let i = 0; i < 3; i++) {
      const part1 = parts1[i] || 0;
      const part2 = parts2[i] || 0;

      if (part1 > part2) return 1;
      if (part1 < part2) return -1;
    }

    return 0;
  }

  isNewerVersion(version: string): boolean {
    return this.compareVersions(version, this.currentVersion.version) > 0;
  }

  getVersionType(oldVersion: string, newVersion: string): 'major' | 'minor' | 'patch' {
    const [oldMajor, oldMinor, oldPatch] = oldVersion.split('.').map(Number);
    const [newMajor, newMinor, newPatch] = newVersion.split('.').map(Number);

    if (newMajor > oldMajor) return 'major';
    if (newMinor > oldMinor) return 'minor';
    return 'patch';
  }

  async getChangelog(): Promise<ChangelogEntry[]> {
    try {
      const response = await fetch('/CHANGELOG.json');
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Failed to fetch changelog:', error);
    }
    return [];
  }

  getVersionBadgeColor(): string {
    const env = this.currentVersion.environment;
    switch (env) {
      case 'production': return 'green';
      case 'staging': return 'yellow';
      case 'development': return 'blue';
      default: return 'gray';
    }
  }
}

export const versionService = new VersionService();